// Charging Simulator: field metadata, chemistry/charger options, vehicle presets, and
// client-side per-field validation. Numeric field keys mirror
// backend/simulation/schemas.py's ChargingSimulationRequest, except the per-charger-kind
// form fields (dc_/ac_charger_power_kw, vehicle_max_dc_kw / vehicle_obc_kw), which
// ChargingSimulator.jsx maps onto charger_power_kw / vehicle_max_charge_power_kw.

import { validateField as _validateField, toRawStrings } from './simulationTools';

export { toRawStrings };

export const CHEMISTRY_OPTIONS = [
  { value: 'LFP', label: 'LFP (Lithium Iron Phosphate)' },
  { value: 'NMC', label: 'NMC (Nickel Manganese Cobalt)' },
  { value: 'NCA', label: 'NCA (Nickel Cobalt Aluminum)' },
  { value: 'LTO', label: 'LTO (Lithium Titanate)' },
];

export const CHARGER_KIND_OPTIONS = [
  { value: 'DC', label: 'DC Fast Charger' },
  { value: 'AC', label: 'AC Charger' },
];

// AC supply (EVSE / wallbox) power levels -- DC uses a free-entry kW field instead. Covers
// Level 1 (120 V household outlet) through 3-phase 22 kW. The vehicle's own onboard-charger
// rating (VEHICLE_OBC_FIELD_SPEC) caps whatever the EVSE can supply.
export const AC_POWER_OPTIONS = [
  { value: '1.4', label: '1.4 kW (Level 1, 120 V / 12 A)' },
  { value: '1.9', label: '1.9 kW (Level 1, 120 V / 16 A)' },
  { value: '3.7', label: '3.7 kW (1-phase, 230 V / 16 A)' },
  { value: '7.4', label: '7.4 kW (1-phase, 230 V / 32 A)' },
  { value: '11', label: '11 kW (3-phase, 16 A)' },
  { value: '22', label: '22 kW (3-phase, 32 A)' },
];

// Required numeric fields (always validated). Capacity is USABLE capacity -- the energy
// between 0% and 100% displayed SoC, net of the manufacturer's hidden top/bottom buffer.
export const REQUIRED_FIELD_SPECS = [
  { key: 'capacity_kwh', label: 'Usable Battery Capacity', unit: 'kWh', min: 1, max: 500, strictlyPositive: true },
  { key: 'nominal_voltage_v', label: 'Nominal Pack Voltage', unit: 'V', min: 12, max: 1000, strictlyPositive: true },
  { key: 'start_soc', label: 'Start SoC', unit: '%', min: 0, max: 99, strictlyPositive: false },
  { key: 'target_soc', label: 'Target SoC', unit: '%', min: 1, max: 100, strictlyPositive: true },
];

export const SOH_FIELD_SPEC = { key: 'state_of_health_pct', label: 'State of Health', unit: '%', min: 50, max: 100, strictlyPositive: true };

export const DC_CHARGER_POWER_FIELD_SPEC = { key: 'dc_charger_power_kw', label: 'Charger Power', unit: 'kW', min: 1, max: 400, strictlyPositive: true };

// DC station output limits. A charger's kW rating is only reachable within its current and
// voltage window -- e.g. a 50 kW unit is typically 125 A max, so a ~370 V pack gets ~46 kW,
// and a 500 V-max unit can't charge an 800 V-class pack at all. Auto-filled from
// typicalDcChargerLimits() below until the user edits them.
export const DC_CHARGER_LIMIT_FIELD_SPECS = [
  { key: 'dc_charger_max_current_a', label: 'Charger Max Current', unit: 'A', min: 10, max: 2000, strictlyPositive: true },
  { key: 'dc_charger_max_voltage_v', label: 'Charger Max Voltage', unit: 'V', min: 50, max: 1500, strictlyPositive: true },
];

// Vehicle-side charge ceilings, filled by vehicle presets. Optional: blank means the battery's
// own C-rate / BMS limits are the only vehicle-side cap.
export const VEHICLE_MAX_DC_FIELD_SPEC = { key: 'vehicle_max_dc_kw', label: 'Vehicle Max DC Charge Power', unit: 'kW', min: 1, max: 1000, strictlyPositive: true, optional: true };
export const VEHICLE_OBC_FIELD_SPEC = { key: 'vehicle_obc_kw', label: 'Vehicle Onboard Charger Rating', unit: 'kW', min: 1, max: 44, strictlyPositive: true, optional: true };

/**
 * Typical output limits for a DC charger of a given power class (public-network hardware;
 * the user can override both). 50 kW-class units are 125 A / 500 V; ~150 kW units ~375 A /
 * 920 V; 350 kW HPC units 500 A / 920 V with liquid-cooled cables.
 */
export function typicalDcChargerLimits(powerKw) {
  if (!(powerKw > 0)) return null;
  if (powerKw <= 60) return { currentA: 125, voltageV: 500 };
  if (powerKw <= 160) return { currentA: 375, voltageV: 920 };
  return { currentA: 500, voltageV: 920 };
}

// Ambient Temperature: always visible and required (not tucked into an "Advanced"/optional
// section) since it's the main lever a user has over the DCIR-power-map/thermal model's
// behavior — see charging.py's simulate_charging(), which starts the whole session (including
// pack temperature) at Start SoC precisely so this field visibly shapes the charted
// current/voltage curve. DEFAULT_INPUTS below pre-fills a real value (20 C) so the field is
// never blank on load.
export const AMBIENT_TEMP_FIELD_SPEC = { key: 'ambient_temp_c', label: 'Ambient Temperature', unit: '°C', min: -40, max: 70, strictlyPositive: false };

// Thermal-management fields: the pack/coolant thermal model (see backend/simulation/
// charging_thermal.py) that directly feeds the DCIR lookup driving charging current/time --
// not a cosmetic side panel. Always active with real default values (DEFAULT_INPUTS below),
// not an optional/blank-means-default section, so these are validated the same way as
// REQUIRED_FIELD_SPECS (see validateRequiredField). battery_temp_c's default tracks Ambient
// Temperature until the user edits it directly -- see ChargingSimulator.jsx.
export const THERMAL_FIELD_SPECS = [
  { key: 'battery_temp_c', label: 'Starting Battery Temperature', unit: '°C', min: -40, max: 70, strictlyPositive: false },
  { key: 'cooling_power_kw', label: 'Cooling System Power', unit: 'kW', min: 0.1, max: 50, strictlyPositive: true },
  { key: 'cooling_cop', label: 'Cooling Coefficient of Performance', unit: '×', min: 0.5, max: 8, strictlyPositive: true },
  { key: 'heating_power_kw', label: 'Heating System Power', unit: 'kW', min: 0.1, max: 50, strictlyPositive: true },
  { key: 'heating_cop', label: 'Heating Coefficient of Performance', unit: '×', min: 0.2, max: 3, strictlyPositive: true },
];

export function validateRequiredField(spec, rawValue) {
  return _validateField(spec, rawValue);
}

export const DEFAULT_INPUTS = {
  capacity_kwh: 55,
  nominal_voltage_v: 400,
  state_of_health_pct: 100,
  chemistry: 'NMC',
  charger_kind: 'DC',
  dc_charger_power_kw: 50,
  dc_charger_max_current_a: 125,
  dc_charger_max_voltage_v: 500,
  ac_charger_power_kw: '7.4',
  vehicle_max_dc_kw: '',
  vehicle_obc_kw: 11,
  start_soc: 20,
  target_soc: 80,
  ambient_temp_c: 20,
  battery_temp_c: 20,
  cooling_power_kw: 5,
  cooling_cop: 2.5,
  heating_power_kw: 5,
  heating_cop: 0.95,
};

// Vehicle presets: real published specs where available, each field's sourcing noted below.
// Auto-fills usable capacity/voltage/chemistry and the vehicle's own charge ceilings (max DC
// power, onboard-charger rating) on selection -- NOT the charger, which is whatever the user
// plugs into. All fields remain editable afterward. capacityKwh is USABLE capacity (gross
// figures are listed in estimatedFields notes where only gross was published).
export const VEHICLE_PRESETS = [
  {
    value: 'tesla_model_y',
    label: 'Tesla Model Y (Long Range AWD)',
    capacityKwh: 75,
    nominalVoltageV: 360,
    chemistry: 'NCA',
    dcMaxPowerKw: 250,
    acMaxPowerKw: 11,
    estimatedFields: ['nominalVoltageV'], // pack nominal voltage varies by source/market; not a single published figure
  },
  {
    value: 'hyundai_ioniq5',
    label: 'Hyundai Ioniq 5 (Long Range AWD, 800V)',
    capacityKwh: 74,
    nominalVoltageV: 697,
    chemistry: 'NMC',
    dcMaxPowerKw: 235,
    acMaxPowerKw: 11,
    estimatedFields: ['capacityKwh'], // 77.4 kWh is the published gross figure; ~74 kWh usable per independent measurements
  },
  {
    value: 'tata_curvv_ev',
    label: 'Tata Curvv EV (55 kWh Long Range)',
    capacityKwh: 55,
    nominalVoltageV: 350,
    chemistry: 'LFP',
    dcMaxPowerKw: 70,
    acMaxPowerKw: 7.2,
    estimatedFields: ['nominalVoltageV', 'capacityKwh'], // voltage not published; 55 kWh not stated as gross vs usable
  },
  {
    value: 'mg_zs_ev',
    label: 'MG ZS EV (Long Range)',
    capacityKwh: 68.3,
    nominalVoltageV: 400,
    chemistry: 'NMC',
    dcMaxPowerKw: 80,
    acMaxPowerKw: 11,
    estimatedFields: ['capacityKwh'], // 72.6 kWh is the published gross figure
  },
  {
    value: 'byd_atto3',
    label: 'BYD Atto 3',
    capacityKwh: 60.5,
    nominalVoltageV: 403,
    chemistry: 'LFP',
    dcMaxPowerKw: 88,
    acMaxPowerKw: 7,
    estimatedFields: ['capacityKwh'], // published 60.48 kWh; LFP packs carry little hidden buffer
  },
];

export const VEHICLE_PRESET_BY_VALUE = Object.fromEntries(VEHICLE_PRESETS.map((p) => [p.value, p]));
